import { useState, useEffect, useRef } from 'react';
import { getCompanyEventData } from '../../api/company.api';
import {
  CompanyEvent,
  CompanyEventResponse,
  UseCompanyEventResult,
} from '../../types/companyevent.type';
import { EventType, WidgetMessageType } from '../../utils/type';
import { WidgetError, WidgetMessage } from '../../types/common.type';

const useCompanyEvent = (
  ric: string = '',
  eventType: EventType = EventType.All,
  token: string = '',
  pageNum: number = 1,
  pageSize: number = 10,
  onError?: (error: WidgetError) => void,
  onNoData?: (message: WidgetMessage) => void,
): UseCompanyEventResult => {
  const [data, setData] = useState<CompanyEvent[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onErrorRef = useRef(onError);
  const onNoDataRef = useRef(onNoData);
  onErrorRef.current = onError;
  onNoDataRef.current = onNoData;

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    getCompanyEventData(ric, eventType, token, pageNum, pageSize)
      .then((res: CompanyEventResponse) => {
        if (!cancelled) {
          setData(res?.data?.items ?? []);
          setTotalItems(res?.data?.totalItems ?? 0);
          if (!res?.data?.items?.length && !res.data.totalItems) {
            onNoDataRef.current?.({
              type: WidgetMessageType.NoData,
              source: 'CompanyEventWidget',
              data: null,
            });
          }
        }
      })
      .catch((err: Error & { code?: string | number }) => {
        if (!cancelled) {
          setError(err.message);
          onErrorRef.current?.({
            message: err?.message,
            type: `${err?.name}_${err?.code}`,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ric, eventType, token, pageNum, pageSize]);

  return { data, totalItems, isLoading, error };
};

export default useCompanyEvent;
